module Meevo.Plugins.InventoryReturns {
    "use strict";

    import IInventoryCountProductSearchCriteria = Meevo.Model.IInventoryCountProductSearchCriteria;
    import IInventoryReturn = Meevo.Model.IInventoryReturn;
    import IInventoryReturnLineItem = Meevo.Model.IInventoryReturnLineItem;
    import ProductInventoryTypeEnum = Meevo.Model.ProductInventoryTypeEnum;
    import ReturnReasonEnum = Meevo.Model.ReturnReasonEnum;

    /**
    * Main Section of Client 
    */
    export class MainSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<IInventoryReturn> {
        public referenceNumberRegExp: RegExp = new RegExp('^[a-zA-Z0-9]{0,30}$');
        public isDiscountinued: boolean = false;
        public inventoryCountProductCriteria: IInventoryCountProductSearchCriteria = {
            productObjectState: Meevo.Model.ObjectStateEnum.Active,
            variantObjectState: [Meevo.Model.ObjectStateEnum.Active],
            isInventoryTrackable: true,
            floorLocationId: null,
            id: null,
            inventoryCountOnly: false,
            iscounted: null,
            productInventoryType: ProductInventoryTypeEnum.Retail,
            manufacturerIds: [],
            distributorIds: [],
            productCatIds: []
        };
        public itemTypeList = [
            { value: ProductInventoryTypeEnum.Retail, label: 'LABEL_RETAIL' },
            { value: ProductInventoryTypeEnum.ShopSupply, label: 'LABEL_SHOP_SUPPLY' },
            { value: ProductInventoryTypeEnum.Other, label: 'HERO_SUB_MISC_SUPPLIES' }
        ];

        public shopSupply = ProductInventoryTypeEnum.ShopSupply;

        public customFooterInfo: Meevo.Components.Controls.IMaintenanceFormCustomFooterInfo = {
            className: '',
            template: 'customFooterInfo.html',
            visibilityRules: () => true,
            position: 'left'
        };
        public selectedInventoryProduct: IInventoryReturnLineItem;
        public productInventoryType = {
            [ProductInventoryTypeEnum.Retail]: this.$translate.instant("LABEL_RETAIL"),
            [ProductInventoryTypeEnum.ShopSupply]: this.$translate.instant("LABEL_SHOP_SUPPLY"),
            [ProductInventoryTypeEnum.Other]: this.$translate.instant("HEADER_MISCELLANEOUS_SUPPLY_DEFINITIONS")
        };
        public subtotal: number = 0;

        protected decimalPrecision: number;
        public tenantEnableProductUsageTracking: boolean = false;

        constructor(
            sectionId: number,
            displayNameContentKey: string,
            template: string,
            isPrimarySection: boolean,
            handlesOwnCrud: boolean,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            dataService: Meevo.Components.BaseClasses.IDataService,
            identityService: Meevo.Components.Common.IIdentityService,
            noun: Meevo.Model.NounEnum,
            appSettings: Components.Common.IAppSettings,
            protected params,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected $translate: ng.translate.ITranslateService,
            sectionSecurityKey?: string
        ) {
            super(
                sectionId,
                displayNameContentKey,
                template,
                isPrimarySection,
                handlesOwnCrud,
                baseEndpoint,
                sectionEndpoint,
                loggingService,
                dataService,
                identityService,
                noun,
                appSettings,
                sectionSecurityKey);

            this.decimalPrecision = this.appSettings.APP_BUSINFOSETTINGS.generalSettings.inventoryDecimalPrecision || 4;
            this.tenantEnableProductUsageTracking = this.appSettings.APP_BUSINFOSETTINGS.generalSettings.productUsageTracking || false;
        }

        sectionModelRetrieved(): void {
            if (!this.model) return;

            if (this.model.insertItems && this.model.insertItems.length) {
                this.model.insertItems.forEach(i => {
                    i.wholeSale = i.lineTotal / i.returnQuantity;
                });

                this.calculateSubTotal();
            }

            this.model.deleteItems = [];
            this.distributorChanged();
            this.productInventoryTypeChanged();
        }

        /**
        * Base class override which defines what a DEFAULT model looks like.
        */
        createSectionModelInstance(postAdd: (data: any) => any): void {
            this.dataService.getTemplate(this.baseEndpoint).then((data) => {
                this.model = data;
                this.model.insertItems = [];
                this.model.deleteItems = [];
                this.model.tax = 0;
                this.model.distributor = null;
                this.model.productInventoryType = ProductInventoryTypeEnum.Retail;
            });
        }

        /**
         * User has selected another distributor
         */
        public distributorChanged(): void {
            this.selectedInventoryProduct = null;
            if (this.model.distributor) {
                this.inventoryCountProductCriteria.distributorIds.push(this.model.distributor.id);
            }
        }

        public productInventoryTypeChanged(): void {
            this.selectedInventoryProduct = null;
            this.inventoryCountProductCriteria.productInventoryType = this.model.productInventoryType ? this.model.productInventoryType : ProductInventoryTypeEnum.Retail;
        }

        /**
        * Calculate subtotal
        */
        public calculateSubTotal(): void {
            this.subtotal = this.model.insertItems.reduce((sum, item) => sum + Number(item.lineTotal), 0);
        }

        /**
         * Calculate total
         * @param {number} subtotal
         * @returns {number}
         */
        public calculateTotal(subtotal: number): number {
            if (!this.model) return;

            return Number(subtotal) + Number(this.model.tax);
        }

        /**
         * Calculate total line 
         * @param {IInventoryReturnLineItem} item
         */
        public calculateTotalLine(item: IInventoryReturnLineItem): void {
            item.lineTotal = item.returnQuantity * item.wholeSale;
            this.calculateSubTotal();

            if (Number(item.returnQuantity) > Number(item.onHand)) {
                this.modalService.alert('MESSAGE_YOU_ARE_RETURNING_GREATER_THEN_ON_HAND', Components.Modal.ModalTranslationModes.Translate);
            }
        }

        /**
         * Calculate Sale line
         * @param {IInventoryReturnLineItem} item
         */
        public calculateSaleLine(item: IInventoryReturnLineItem): void {
            item.wholeSale = item.lineTotal / item.returnQuantity;
            this.calculateSubTotal();
        }

        /**
         * Add new product to inventory return
         */
        public addNewProduct(): void {
            let { variantId: variant_TId, productInventoryType, onHand, productName, wholeSale, product_TId, displayName, modelQuantity, orderPoint, sku, barCode } = this.selectedInventoryProduct,
                lineItem: IInventoryReturnLineItem = {
                    variant_TId, productInventoryType, onHand, productName, wholeSale, returnQuantity: 1, displayName, product_TId, modelQuantity, orderPoint, sku,barCode
                };

            lineItem.productInventoryType = this.model.productInventoryType;//.inventoryCountProductCriteria.productInventoryType;
            
            lineItem.isNew = true;
            // Automatically Check the Deduct Checkbox U.S.:34775
            lineItem.isDeducted = true;

            lineItem.enableProductUsageTracking = this.selectedInventoryProduct.enableProductUsageTracking;
            lineItem.productUsageType = this.selectedInventoryProduct.productUsageType;
            lineItem.productUnitOfMeasureEnum = this.selectedInventoryProduct.productUnitOfMeasureEnum;
            lineItem.unitOfMeasureQuantity = this.selectedInventoryProduct.unitOfMeasureQuantity;

            this.busyService.start();
            // get particular product in order to use description
            this.dataService.getItem(this.appSettings.API_ENDPOINTS.product, product_TId)
                .then((data: any) => {
                    // clear selected product
                    this.selectedInventoryProduct = null;

                    // update description for product
                    lineItem.productDescription = data.description;

                    this.model.insertItems.push(lineItem);
                    this.calculateTotalLine(lineItem);
                    this.calculateSubTotal();
                })
                .finally(() => {
                    this.busyService.stop();
                });
        }

        /**
         * Remove product variant
         * @param {IInventoryReturnLineItem} item
         */
        public removeProductVariant(item: IInventoryReturnLineItem): void {
            let index = this.model.insertItems.indexOf(item);

            if (index > -1) {
                this.model.insertItems.splice(index, 1);

                if (!item.isNew) {
                    this.model.deleteItems.push(item);
                }
            }
        }

        /**
         * Variant object state setuping
         */
        public discountinuedChanged(): void {
            if (this.isDiscountinued) {
                this.inventoryCountProductCriteria.variantObjectState = [
                    Meevo.Model.ObjectStateEnum.Active,
                    Meevo.Model.ObjectStateEnum.Inactive
                ];
            } else {
                this.inventoryCountProductCriteria.variantObjectState = [
                    Meevo.Model.ObjectStateEnum.Active
                ];
            }
        }

        isProductUsageEnabled(variant: IInventoryReturnLineItem): boolean {
            return this.tenantEnableProductUsageTracking && variant && variant.enableProductUsageTracking && variant.productUsageType == Meevo.Model.ProductUsageAmountTypeEnum.PartialAmount;
        }

        getOnHandDecimalPrecision(item: IInventoryReturnLineItem): number {
            return (!Number.isInteger(item.onHand) || this.isProductUsageEnabled(item)) ? this.decimalPrecision : null;
        }
    }
}